import React, { useState } from "react";
import {
  AbilityPhase,
  AbilityUsage,
  canUseAction,
  ActionTargetType,
  getCoreActions,
  getPlayerActionInCurrentPhase,
} from "./abilities";
import { RolePlayer, AbilityPhaseResult } from "./RoleDistributorReducer";
import { RoleDistributorSocketService } from "./RoleDistributorSocketService";
import { useTranslation } from "react-i18next";
import AbilityTargetSelector from "./AbilityTargetSelector";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  abilityPhase: AbilityPhase;
  currentPlayer: RolePlayer;
  allPlayers: RolePlayer[];
  abilityUsages: AbilityUsage[];
  pastAbilityResults: AbilityUsage[][];
  abilityHistory: AbilityPhaseResult[];
  socketService: RoleDistributorSocketService | null;
  initialTab?: "abilities" | "results";
  seeRolesTargets?: string[]; // 역할 확인 대상 역할 목록
}

const AbilityModal: React.FC<Props> = ({
  isOpen,
  onClose,
  abilityPhase,
  currentPlayer,
  allPlayers,
  abilityUsages,
  pastAbilityResults,
  abilityHistory,
  socketService,
  initialTab = "abilities",
  seeRolesTargets,
}) => {
  const { t } = useTranslation();
  const [selectedAbilityId, setSelectedAbilityId] = useState<string | null>(
    null
  );
  const [showTargetSelector, setShowTargetSelector] = useState(false);
  const [activeTab, setActiveTab] = useState<"abilities" | "results">(
    initialTab
  );
  const [showModifyModal, setShowModifyModal] = useState(false);

  // 모달이 열릴 때마다 초기 탭으로 리셋
  React.useEffect(() => {
    if (isOpen) {
      setActiveTab(initialTab);
    }
  }, [isOpen, initialTab]);

  if (!isOpen) return null;

  // 현재 플레이어가 보유한 액션 목록
  const coreActions = getCoreActions(t);
  const playerActions = coreActions.filter((action) =>
    (currentPlayer.abilities || []).includes(action.id)
  );

  // 타이머 만료 확인
  const isTimerExpired = abilityPhase.endTime && Date.now() > abilityPhase.endTime;

  // 액션 수정 가능 여부 확인
  const canModifyAction = (abilityId: string): boolean => {
    if (!abilityPhase.isActive || isTimerExpired) return false;
    const existingUsage = getPlayerActionInCurrentPhase(
      abilityId,
      currentPlayer.id,
      abilityPhase,
      abilityUsages
    );
    return !!existingUsage;
  };

  const handleAbilityClick = (abilityId: string) => {
    if (!abilityPhase.isActive) {
      alert(t("RoleDistributor.actionPhaseNotActive"));
      return;
    }

    // 타이머 종료 체크
    if (isTimerExpired) {
      alert(t("RoleDistributor.timerExpired", "시간이 종료되었습니다."));
      return;
    }

    const action = playerActions.find((a) => a.id === abilityId);
    if (!action) return;

    // 이미 사용한 액션인지 확인
    const existingUsage = getPlayerActionInCurrentPhase(
      abilityId,
      currentPlayer.id,
      abilityPhase,
      abilityUsages
    );

    if (existingUsage) {
      // 이미 사용한 액션 -> 수정 모달 표시
      setSelectedAbilityId(abilityId);
      setShowModifyModal(true);
      return;
    }

    setSelectedAbilityId(abilityId);

    // 대상 선택이 필요한 액션인지 확인
    if (action.targetType === ActionTargetType.PLAYER) {
      setShowTargetSelector(true);
    } else if (action.targetType === ActionTargetType.MULTI_PLAYER) {
      // 다중 플레이어 선택 모달 표시 (nominate 등)
      setShowTargetSelector(true);
    } else if (action.targetType === ActionTargetType.CHOICE) {
      // 찬반투표는 바로 선택 모달 표시
      setShowTargetSelector(true);
    } else if (action.targetType === ActionTargetType.ROLE) {
      // 역할 선택 모달 표시 (see_roles)
      setShowTargetSelector(true);
    } else {
      // 자가 사용 또는 전체 대상 액션은 바로 실행
      executeAbility(abilityId);
    }
  };

  // 액션 취소 핸들러
  const handleCancelAction = () => {
    if (!socketService || !selectedAbilityId) return;

    socketService.cancelAbility(currentPlayer.id, selectedAbilityId);
    setShowModifyModal(false);
    setSelectedAbilityId(null);
  };

  // 액션 수정 핸들러 (취소 후 재선택)
  const handleModifyAction = () => {
    if (!socketService || !selectedAbilityId) return;

    // 기존 액션 취소
    socketService.cancelAbility(currentPlayer.id, selectedAbilityId);

    // 새 대상 선택 모달 표시
    setShowModifyModal(false);

    const action = playerActions.find((a) => a.id === selectedAbilityId);
    if (action) {
      if (
        action.targetType === ActionTargetType.PLAYER ||
        action.targetType === ActionTargetType.MULTI_PLAYER ||
        action.targetType === ActionTargetType.CHOICE ||
        action.targetType === ActionTargetType.ROLE
      ) {
        setShowTargetSelector(true);
      } else {
        // 자가 사용 또는 전체 대상 액션은 바로 실행
        executeAbility(selectedAbilityId);
      }
    }
  };

  // 액션 사용 정보 텍스트 생성
  const getUsageDisplayText = (usage: AbilityUsage): string => {
    const parts: string[] = [];

    if (usage.targetId) {
      const target = allPlayers.find((p) => p.id === usage.targetId);
      parts.push(`→ ${target?.name || "Unknown"}`);
    }

    if (usage.targetIds && usage.targetIds.length > 0) {
      const targetNames = usage.targetIds
        .map((id) => allPlayers.find((p) => p.id === id)?.name || "Unknown")
        .join(", ");
      parts.push(`→ ${targetNames}`);
    }

    if (usage.choice !== undefined) {
      parts.push(usage.choice ? "👍" : "👎");
    }

    if (usage.selectedRoles && usage.selectedRoles.length > 0) {
      parts.push(`[${usage.selectedRoles.join(", ")}]`);
    }

    return parts.join(" ");
  };

  const executeAbility = (
    abilityId: string,
    targetId?: number,
    choice?: boolean,
    selectedRole?: string,
    selectedRoles?: string[],
    targetIds?: number[]
  ) => {
    if (!socketService) return;

    socketService.useAbility({
      playerId: currentPlayer.id,
      abilityId,
      targetId,
      targetIds,
      choice,
      selectedRole,
      selectedRoles,
      timestamp: Date.now(),
    });

    setSelectedAbilityId(null);
    setShowTargetSelector(false);
  };

  const handleTargetSelected = (targetId?: number, choice?: boolean, selectedRole?: string, selectedRoles?: string[], targetIds?: number[]) => {
    if (selectedAbilityId) {
      executeAbility(selectedAbilityId, targetId, choice, selectedRole, selectedRoles, targetIds);
    }
  };

  const handleCancelTargetSelection = () => {
    setSelectedAbilityId(null);
    setShowTargetSelector(false);
  };

  // 현재 플레이어의 능력 사용 기록 필터링
  const myUsageHistory = abilityUsages.filter(
    (usage) => usage.playerId === currentPlayer.id
  );

  return (
    <>
      <div className="ability-modal-overlay" onClick={onClose}>
        <div className="ability-modal" onClick={(e) => e.stopPropagation()}>
          {/* 모달 헤더 */}
          <div className="ability-modal-header">
            <h3>{t("RoleDistributor.specialActions")}</h3>
            <button className="close-button" onClick={onClose}>
              <i className="fas fa-times" />
            </button>
          </div>

          {/* 탭 네비게이션 */}
          <div className="ability-modal-tabs">
            <button
              className={`tab-button ${
                activeTab === "abilities" ? "active" : ""
              }`}
              onClick={() => setActiveTab("abilities")}
            >
              <i className="fas fa-bolt mr-2" />
              {t("RoleDistributor.myActions")}
            </button>
            <button
              className={`tab-button ${
                activeTab === "results" ? "active" : ""
              }`}
              onClick={() => setActiveTab("results")}
            >
              <i className="fas fa-history mr-2" />
              {t("RoleDistributor.actionResults")}
              {abilityHistory.length > 0 && (
                <span className="ml-1 px-1.5 py-0.5 bg-red-500 text-white rounded-full text-xs font-semibold">
                  {abilityHistory.length}
                </span>
              )}
            </button>
          </div>

          {/* 탭 컨텐츠 */}
          {activeTab === "abilities" ? (
            <>
              {/* 상단 고정 영역 - 사용 가능한 능력 */}
              <div className="ability-modal-abilities">
                <div className="phase-status">
                  {abilityPhase.isActive ? (
                    <span className="phase-active">
                      🟢 {t("RoleDistributor.canUseActions")}
                    </span>
                  ) : (
                    <span className="phase-inactive">
                      🔴 {t("RoleDistributor.waitForPhase")}
                    </span>
                  )}
                </div>

                <div className="ability-list">
                  {playerActions.length > 0 ? (
                    playerActions.map((action) => {
                      const existingUsage = getPlayerActionInCurrentPhase(
                        action.id,
                        currentPlayer.id,
                        abilityPhase,
                        abilityUsages
                      );
                      const hasUsed = !!existingUsage;
                      const canModify = canModifyAction(action.id);
                      const canUse =
                        abilityPhase.isActive &&
                        !isTimerExpired &&
                        canUseAction(
                          action.id,
                          currentPlayer.id,
                          abilityPhase,
                          abilityUsages
                        );

                      // 사용된 액션의 대상/선택 정보 표시
                      const usageInfo = existingUsage
                        ? getUsageDisplayText(existingUsage)
                        : null;

                      return (
                        <div
                          key={action.id}
                          className={`ability-item compact ${
                            !canUse && !canModify ? "disabled" : ""
                          } ${hasUsed ? "used" : ""} ${
                            canModify ? "modifiable" : ""
                          }`}
                          onClick={() => {
                            if (canModify || canUse) {
                              handleAbilityClick(action.id);
                            }
                          }}
                        >
                          <div className="ability-icon">{action.icon}</div>
                          <div className="ability-info">
                            <div className="ability-name">{action.name}</div>
                            {usageInfo && (
                              <div className="ability-usage-info">
                                {usageInfo}
                              </div>
                            )}
                          </div>
                          <div className="ability-status">
                            {hasUsed && (
                              <span
                                className={`used-badge ${
                                  canModify ? "modifiable" : ""
                                }`}
                              >
                                {canModify
                                  ? t("RoleDistributor.tapToModify", "수정하기")
                                  : t("RoleDistributor.used", "사용됨")}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="no-abilities">
                      {t(
                        "RoleDistributor.noActions",
                        "보유한 액션이 없습니다."
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* 하단 스크롤 영역 - 사용 기록 */}
              <div className="ability-modal-history">
                <h4>{t("RoleDistributor.actionHistory", "액션 사용 기록")}</h4>
                <div className="history-list">
                  {myUsageHistory.length > 0 ? (
                    myUsageHistory.reverse().map((usage, index) => {
                      const action = coreActions.find(
                        (a) => a.id === usage.abilityId
                      );
                      const target = usage.targetId
                        ? allPlayers.find((p) => p.id === usage.targetId)
                        : null;

                      return (
                        <div key={index} className="history-item">
                          <div className="history-time">
                            {new Date(usage.timestamp).toLocaleTimeString()}
                          </div>
                          <div className="history-content">
                            <span className="history-ability">
                              {action?.icon} {action?.name}
                            </span>
                            {target && (
                              <span className="history-target">
                                <i className="fas fa-arrow-right" />{" "}
                                {target.name}
                              </span>
                            )}
                            {usage.choice !== undefined && (
                              <span className="history-choice">
                                <i className="fas fa-arrow-right" />{" "}
                                {usage.choice ? "👍" : "👎"}
                              </span>
                            )}
                            {usage.selectedRoles && usage.selectedRoles.length > 0 && (
                              <span className="history-roles">
                                <i className="fas fa-arrow-right" />{" "}
                                {usage.selectedRoles.join(", ")}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="no-history">
                      {t(
                        "RoleDistributor.noHistory",
                        "아직 사용한 액션이 없습니다."
                      )}
                    </div>
                  )}
                </div>
              </div>
            </>
          ) : (
            /* 결과 기록 탭 */
            <div className="ability-modal-results">
              <div className="results-content">
                {abilityHistory.length > 0 ? (
                  abilityHistory.map((phaseResult, phaseIndex) => {
                    // 현재 플레이어에게 표시할 결과만 필터링
                    const visibleResults = phaseResult.results.filter(
                      (result) => {
                        // 공개 결과는 모두에게 표시
                        if (result.type === "public") return true;

                        // 비공개 결과는 해당 플레이어에게만 표시
                        return (
                          result.type === "private" &&
                          result.targetPlayerId === currentPlayer.id
                        );
                      }
                    );

                    if (visibleResults.length === 0) return null;

                    return (
                      <div key={phaseIndex} className="results-round">
                        <h5 className="round-title">
                          {t("RoleDistributor.result", "Result")}{" "}
                          {abilityHistory.length - phaseIndex}
                        </h5>
                        <div className="results-list">
                          {visibleResults.map((result, index) => {
                            const action = result.abilityId
                              ? coreActions.find(
                                  (a) => a.id === result.abilityId
                                )
                              : null;

                            return (
                              <div
                                key={index}
                                className={`result-item ${result.type}`}
                              >
                                <div className="result-header">
                                  {action && (
                                    <span className="result-ability">
                                      {action.icon} {action.name}
                                    </span>
                                  )}
                                  <span
                                    className={`result-visibility ${result.type}`}
                                  >
                                    {result.type === "public"
                                      ? t(
                                          "RoleDistributor.publicResults",
                                          "공개"
                                        )
                                      : t(
                                          "RoleDistributor.yourResults",
                                          "개인"
                                        )}
                                  </span>
                                </div>
                                <div className="result-message">
                                  {result.message}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="no-results">
                    {t("RoleDistributor.noResults", "표시할 결과가 없습니다.")}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 대상 선택 모달 */}
      {showTargetSelector && selectedAbilityId && (
        <AbilityTargetSelector
          ability={playerActions.find((a) => a.id === selectedAbilityId)!}
          availablePlayers={allPlayers}
          currentPlayerId={currentPlayer.id}
          seeRolesTargets={seeRolesTargets}
          onTargetSelected={handleTargetSelected}
          onCancel={handleCancelTargetSelection}
        />
      )}

      {/* 액션 수정/취소 모달 */}
      {showModifyModal && selectedAbilityId && (
        <div
          className="modify-action-modal-overlay"
          onClick={() => {
            setShowModifyModal(false);
            setSelectedAbilityId(null);
          }}
        >
          <div
            className="modify-action-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <h4>{t("RoleDistributor.modifyAction", "액션 수정")}</h4>
            <p>
              {t(
                "RoleDistributor.modifyActionDesc",
                "이 액션을 수정하거나 취소할 수 있습니다."
              )}
            </p>

            <div className="modify-button-group">
              <button onClick={handleModifyAction} className="modify-btn">
                <i className="fas fa-edit" />
                {t("RoleDistributor.changeTarget", "대상 변경")}
              </button>
              <button onClick={handleCancelAction} className="cancel-btn">
                <i className="fas fa-times" />
                {t("RoleDistributor.cancelAction", "취소하기")}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AbilityModal;
